import * as React from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import user from 'js/lib/user';

import type {
  ProgramCurriculumProductsV1GetQuery as ProgramCurriculumProductsQueryType,
  ProgramCurriculumProductsV1GetQueryVariables as ProgramCurriculumProductsQueryVariables,
  ProgramUserProductAction,
} from 'bundles/naptimejs/resources/__generated__/ProgramCurriculumProductsV1';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import EnterpriseUserProductInfoContext from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseUserProductInfoContext';
import {
  UnifiedEnterpriseOnDemandSpecialziationsMembershipsQuery,
  UnifiedEnterpriseOpenCourseMembershipsQuery,
} from 'bundles/unified-description-page-common/queries/ProductMembershipQueries';
import type {
  UnifiedEnterpriseOnDemandSpecialziationsMembershipsQuery as UnifiedEnterpriseOnDemandSpecialziationsMembershipsQueryType,
  UnifiedEnterpriseOnDemandSpecialziationsMembershipsQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/UnifiedEnterpriseOnDemandSpecialziationsMembershipsQuery';
import type {
  UnifiedEnterpriseOpenCourseMembershipsQuery as UnifiedEnterpriseOpenCourseMembershipsQueryType,
  UnifiedEnterpriseOpenCourseMembershipsQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/UnifiedEnterpriseOpenCourseMembershipsQuery';

const SPECIALIZATION_TYPE_ID = 'Specialization';

const COURSE_TYPE_ID = 'VerifiedCertificate';

// List of available actions for users in "audit" mode (i.e. enrolled via program they're no longer a member of)
const AUDIT_MODE_STATES = ['SELECT', 'ENROLL'] as ProgramUserProductAction[];

const UnifiedEnterpriseProgramCurriculumProductsQuery = gql`
  query UnifiedEnterpriseProgramCurriculumProductsQuery($id: String!) {
    ProgramCurriculumProductsV1 @naptime {
      get(id: $id) {
        elements {
          id
          productState {
            definition {
              isWishlisted
              enrolledAt
              state
              actions
            }
          }
        }
      }
    }
  }
`;

const shapeSpecializationIdForQuery = (specializationId?: string) => {
  // Ensure s12ns with versioned ID are in the correct format.
  if (specializationId?.includes('child~')) {
    return `child!~${specializationId.split('child~')[1]}`;
  } else {
    return specializationId;
  }
};

const useProgramCurriculumProductsQuery = (productType: string, programId?: string, productId?: string) => {
  return useQuery<ProgramCurriculumProductsQueryType, ProgramCurriculumProductsQueryVariables>(
    UnifiedEnterpriseProgramCurriculumProductsQuery,
    {
      variables: {
        id: `${programId}~${productType}~${productId}`,
      },
      skip: !programId || !productId || !user.isAuthenticatedUser(),
    }
  );
};

export const EnterpriseUserProductCourseInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const program = useProgram();
  const pageData = useEnterpriseCoursePageData();
  const userId = user.get()?.id;
  const courseMembershipResult = useQuery<
    UnifiedEnterpriseOpenCourseMembershipsQueryType,
    UnifiedEnterpriseOpenCourseMembershipsQueryVariables
  >(UnifiedEnterpriseOpenCourseMembershipsQuery, {
    variables: {
      userId: userId?.toString(),
      // @ts-expect-error checked in skip
      slug: pageData?.slug,
    },
    skip: !userId || !pageData?.slug,
  });
  const programCurriculumProductsResult = useProgramCurriculumProductsQuery(COURSE_TYPE_ID, program?.id, pageData?.id);
  const enrolledAt =
    programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState?.definition
      ?.enrolledAt;
  const productActions =
    programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState?.definition
      ?.actions;
  const productIsAvailableWithActions =
    productActions &&
    programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState?.definition
      ?.state === 'AVAILABLE';

  return (
    <EnterpriseUserProductInfoContext.Provider
      value={{
        isWishlisted:
          !!programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState
            ?.definition?.isWishlisted,
        isEnrolled:
          // enrolledAt is a timestamp and can be 0 (which is valid in our case).
          enrolledAt !== undefined && enrolledAt !== null,
        isEnrolledOutsideOfProgram:
          courseMembershipResult?.data?.OpenCourseMembershipsV1Resource?.findByUserAndSlug?.elements?.[0]
            ?.courseRole === 'LEARNER',
        // If the product state is available and the learner is able to select and enroll, they are able to audit the course.
        // This occurs when the user enrolled in a course via a program they are no longer a member of.
        isAuditing:
          productIsAvailableWithActions && AUDIT_MODE_STATES.every((action) => productActions.includes(action)),
        loading: courseMembershipResult?.loading,
      }}
    >
      {children}
    </EnterpriseUserProductInfoContext.Provider>
  );
};

export const EnterpriseUserProductSpecializationInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const program = useProgram();
  const pageData = useEnterpriseSpecializationPageData();
  const specializationMembershipResult = useQuery<
    UnifiedEnterpriseOnDemandSpecialziationsMembershipsQueryType,
    UnifiedEnterpriseOnDemandSpecialziationsMembershipsQueryVariables
  >(UnifiedEnterpriseOnDemandSpecialziationsMembershipsQuery, {
    variables: {
      // @ts-expect-error checked in skip
      s12nId: pageData?.id,
    },
    skip: !pageData?.id,
  });
  const programCurriculumProductsResult = useProgramCurriculumProductsQuery(
    SPECIALIZATION_TYPE_ID,
    program?.id,
    shapeSpecializationIdForQuery(pageData?.id)
  );
  const enrolledAt =
    programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState?.definition
      ?.enrolledAt;

  return (
    <EnterpriseUserProductInfoContext.Provider
      value={{
        isWishlisted:
          !!programCurriculumProductsResult?.data?.ProgramCurriculumProductsV1?.get?.elements?.[0]?.productState
            ?.definition?.isWishlisted,
        isEnrolled:
          // enrolledAt is a timestamp and can be 0 (which is valid in our case).
          enrolledAt !== undefined && enrolledAt !== null,
        isEnrolledOutsideOfProgram:
          !!specializationMembershipResult?.data?.OnDemandSpecializationMembershipsV1Resource?.meByS12nId?.elements?.[0]
            ?.id,
      }}
    >
      {children}
    </EnterpriseUserProductInfoContext.Provider>
  );
};
